// Imports
import { faCalendarRange, faLocationDot, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faChevronRight, faQuestion, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tippy from '@tippyjs/react';
import { darken } from 'polished';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { Params, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import ButtonGroup from '../../../../../../shareables/foundation/front-end/components/buttons/group';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { fieldContainerBottomMargin } from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import tippyProps from '../../../../../../shareables/foundation/front-end/utils/tippy-props';
import Modal from '../../../../../../shareables/front-end/modal';
import modalHelpers from '../../../../../../shareables/front-end/modal/modal-helpers';
import APIButton from '../../../../../../shareables/integrations/api/components/button';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetEventResponse } from '../../../../../../shareables/types/api/main/website/page/type/202/event/get';
import { GetOccurrenceResponse } from '../../../../../../shareables/types/api/main/website/page/type/202/event/occurrence/get';
import { PostRSVPParameters } from '../../../../../../shareables/types/api/main/website/page/type/202/event/occurrence/rsvp/post';
import { PutRSVPParameters } from '../../../../../../shareables/types/api/main/website/page/type/202/event/occurrence/rsvp/put';
import formatDateRange from '../../../../../../shareables/utils/format-date-range';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import SignInAnchor from '../../../../sign-in-anchor';
import Attachments from '../shared-components/attachments';
import { StyledEditorGeneratedContentContainer } from '../shared-components/styled-editor-generated-content-container';
import ConditionalTooltip from '../shared-components/conditional-tooltip';
import formatEventURI from './utils/format-event-title';
import { Helmet } from 'react-helmet-async';
import ResponsesTable from './responses-table';


// Styled components
const Wrapper = styled.div<{ theme: GeneratedThemeProps }>`
	a {
		color: ${(props) => props.theme.contentLinkColor};
		
		&:hover,
		&:focus {
			text-decoration: none;
		}
	}
	
	hr {
		margin: 1rem 0;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

const Details = styled.div`
	margin-top: -0.25rem;
	margin-right: 1rem;
`;

const When = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.95rem;
	margin-bottom: 0.5rem;
`;

const Location = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.95rem;
	margin-bottom: 0.5rem;
`;

const Attendance = styled.div<{ isOpen: boolean; hasResponses: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.9rem;
	
	.fa-chevron-right {
		transition: transform 0.1s;
	}
	
	${(props) =>
		props.hasResponses &&
		css`
			cursor: pointer;
		`}
	
	${(props) =>
		props.isOpen &&
		css`
			.fa-chevron-right {
				transform: rotate(90deg);
				margin-right: 0.25rem;
			}
		`}
`;

const Heading = styled.h3`
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 1.1;
	margin-bottom: 0.25rem;
`;

const Notes = styled(StyledEditorGeneratedContentContainer)<{ hasAttachment: boolean }>`
	font-size: 0.95rem;
	line-height: 1.6;
	
	p {
		margin-bottom: ${(props) => (props.hasAttachment ? 0 : '0.5rem')};
	}
`;

const RSVP = styled.div<{ theme: GeneratedThemeProps; hasLimit: boolean }>`
	background: hsl(0, 0%, 97%);
	padding: 0.5rem;
	border: 0.0625rem solid #eee;
	border-radius: 0.4rem;
	margin-top: 1rem;
	
	em {
		display: block;
		text-align: center;
	}
	
	button[type='submit'] {
		background-color: ${(props) => props.theme.baseColor};
		
		&:hover,
		&:focus {
			background-color: ${(props) => darken(0.05, props.theme.baseColor)} !important;
		}
	}
	
	form {
		padding-top: 0.5rem;
		margin-bottom: ${({ hasLimit }) => (hasLimit ? fieldContainerBottomMargin : '')};
	}
`;

const RSVPButtonGroup = styled(ButtonGroup)`
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	margin-top: 1rem;
	margin-bottom: 0.25rem;
	
	button {
		gap: 0.3rem;
	}
`;

const Limit = styled.small`
	display: block;
	text-align: center;
`;


// Map from URL response param to RSVP response type
const responseParamMap: Record<'0' | '1' | '2', PostRSVPParameters['response']> = {
	'0': 'NOT_GOING',
	'1': 'GOING',
	'2': 'MAYBE',
};


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	onReady: () => void;
	storybookEvent?: GetEventResponse;
	storybookOccurrence?: GetOccurrenceResponse;
	storybookShowRSVPPrompt?: true;
	storybookShowRSVPResponses?: true;
}


// Function component
const EventDetailsModal: React.FC<Props> = ({
	pageID,
	pageTitle,
	onReady,
	storybookEvent,
	storybookOccurrence,
	storybookShowRSVPPrompt,
	storybookShowRSVPResponses,
}) => {
	// Use state
	const [event, setEvent] = useState<GetEventResponse | null>(storybookEvent ?? null);
	const [occurrence, setOccurrence] = useState<GetOccurrenceResponse | null>(storybookOccurrence ?? null);
	const [showRSVPPrompt, setShowRSVPPrompt] = useState(storybookShowRSVPPrompt ?? false);
	const [showRSVPResponses, setShowRSVPResponses] = useState(storybookShowRSVPResponses ?? false);
	const [isModalReady, setIsModalReady] = useState(false);
	const [autoRSVPResponse, setAutoRSVPResponse] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		const responseParam = params.get('response');
		const isKnownResponseParam = (value: string | null): value is '0' | '1' | '2' =>
			value !== null && value in responseParamMap;
		
		return params.get('rsvp') === '1' && isKnownResponseParam(responseParam) ? responseParamMap[responseParam] : null;
	});
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const website = useAppSelector((state) => state.self.website);
	const user = useAppSelector((state) => state.common.user);
	const communityName = useAppSelector((state) => state.self.website?.communityName);
	
	
	// Use React Router functionality
	const { eventAndOccurrenceIdentifier } = useParams<Params>();
	const navigate = useNavigate();
	const location = useLocation();
	
	
	// Use ref
	const promptAnswerSetter = useRef<(value: string) => void>();
	const closeMethodRef = useRef<'mouse'>();
	
	
	// Parse event and occurrence IDs from params
	const match = eventAndOccurrenceIdentifier?.match(/-(\d+)_(\d+)$/);
	
	const eventID = Number(match?.[1]);
	const occurrenceID = Number(match?.[2]);
	
	
	// Function to load event
	const memoizedLoadEvent = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetEventResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/event/${eventID}`,
				completion: (json) => {
					setEvent(json.response);
				},
			})
		);
	}, [pageID, eventID, dispatch]);
	
	
	// Function to load occurrence
	const memoizedLoadOccurrence = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetOccurrenceResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/event/${eventID}/occurrence/${occurrenceID}`,
				completion: (json) => {
					setOccurrence(json.response);
				},
			})
		);
	}, [eventID, occurrenceID, dispatch, pageID]);
	
	
	// Load event and occurrence
	useEffect(() => {
		// Return early
		if (isNaN(eventID) || isNaN(occurrenceID)) {
			return;
		}
		
		
		// Load event and occurrence
		memoizedLoadEvent();
		memoizedLoadOccurrence();
	}, [eventID, occurrenceID, memoizedLoadEvent, memoizedLoadOccurrence]);
	
	
	// Notify parent component when the modal is ready
	//  -> Checking for ID prevents flicker of stale data when re-opening modal
	useEffect(() => {
		if (!isModalReady && event && occurrence && event.id === eventID && occurrence.id === occurrenceID) {
			onReady();
			setIsModalReady(true);
		}
	}, [isModalReady, onReady, event, eventID, occurrence, occurrenceID]);
	
	
	// Replace URL with titled version once event is loaded
	useEffect(() => {
		if (!event?.title || isNaN(eventID) || isNaN(occurrenceID)) {
			return;
		}
		
		const expectedIdentifier = `${formatEventURI(event.title)}-${eventID}_${occurrenceID}`;
		
		if (eventAndOccurrenceIdentifier !== expectedIdentifier) {
			navigate(`../${expectedIdentifier}${location.search}`, {
				relative: 'path',
				replace: true,
				state: { disableScrollToTop: true },
			});
		}
	}, [event?.title, eventID, occurrenceID, eventAndOccurrenceIdentifier, navigate, location.search]);
	
	
	// Close modal
	useEffect(() => {
		if ((isNaN(eventID) || isNaN(occurrenceID)) && !(window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			const modalElement = document.getElementById('event-details-modal');
			
			if (modalElement?.classList.contains('is-open')) {
				modalHelpers.close('event-details-modal');
				
				if (closeMethodRef.current === 'mouse' && document.activeElement instanceof HTMLElement) {
					document.activeElement.blur();
				}
			}
			
			setShowRSVPPrompt(false);
			setShowRSVPResponses(false);
			setIsModalReady(false);
		}
	}, [eventID, occurrenceID, setEvent, setOccurrence, setShowRSVPPrompt]);
	
	
	// Reset prompt answer field when hidden
	useEffect(() => {
		if (!showRSVPPrompt && promptAnswerSetter.current) {
			promptAnswerSetter.current('');
		}
	}, [showRSVPPrompt]);
	
	
	// Determine current RSVP status
	const currentRSVPResponse = occurrence?.rsvp?.response;
	
	
	// Helper function to build RSVP button options
	const getRSVPButtonOptions = useCallback(
		(status: 'GOING' | 'MAYBE' | 'NOT_GOING') => {
			// Handle deleting RSVP
			const isAlreadySelected = currentRSVPResponse === status;
			
			if (isAlreadySelected) {
				return {
					action: 'DELETE',
					uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/event/${eventID}/occurrence/${occurrenceID}/rsvp`,
				};
			}
			
			
			// Handle upserting RSVP
			return {
				action: currentRSVPResponse ? 'PUT' : 'POST',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/event/${eventID}/occurrence/${occurrenceID}/rsvp`,
			};
		},
		[currentRSVPResponse, pageID, eventID, occurrenceID]
	);
	
	
	// Auto-submit RSVP when opened via email link
	useEffect(() => {
		// Return early if not ready, or if there's no response, event, occurrence, or user
		if (
			!isModalReady ||
			!autoRSVPResponse ||
			!event ||
			!occurrence ||
			event.id !== eventID ||
			occurrence.id !== occurrenceID ||
			!user.signedIn
		) {
			return;
		}
		
		
		// Remove params from URL to prevent re-triggering this effect
		const newParams = new URLSearchParams(location.search);
		newParams.delete('rsvp');
		newParams.delete('response');
		const newSearch = newParams.toString();
		window.history.replaceState(null, '', `${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
		
		
		// Prevent this effect from firing again for the consumed link
		setAutoRSVPResponse(null);
		
		
		// Return early if the new response is the same
		if (currentRSVPResponse === autoRSVPResponse) {
			return;
		}
		
		
		// Handle case where a prompt needs to be shown
		if (autoRSVPResponse === 'GOING' && event.rsvp?.prompt) {
			setShowRSVPPrompt(true);
			return;
		}
		
		
		// Submit the response
		void dispatch(
			apiActions.call<PostRSVPParameters | PutRSVPParameters, undefined, undefined>({
				...getRSVPButtonOptions(autoRSVPResponse),
				parameters: { response: autoRSVPResponse },
				completion: () => {
					memoizedLoadOccurrence();
				},
			})
		);
	}, [
		isModalReady,
		autoRSVPResponse,
		currentRSVPResponse,
		event,
		eventID,
		occurrence,
		occurrenceID,
		user.signedIn,
		pageID,
		dispatch,
		memoizedLoadOccurrence,
		location.search,
		location.pathname,
		getRSVPButtonOptions,
	]);
	
	
	// Parse and sanitize notes
	const sanitizedNotes = {
		__html: parseAndSanitizeMarkdown(event?.notes ?? ''),
	};
	
	
	// Build form options
	const formOptions: APIFormOptions<PostRSVPParameters | PutRSVPParameters, undefined, undefined> = {
		action: currentRSVPResponse ? 'PUT' : 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/event/${eventID}/occurrence/${occurrenceID}/rsvp`,
		submitButton: {
			text: 'Submit',
		},
		cancelButton: {
			text: 'Cancel',
		},
		extraParameters: {
			response: 'GOING',
		},
	};
	
	
	// Calculate response counts
	const goingCount = occurrence?.rsvps.filter((rsvp) => rsvp.response === 'GOING').length;
	
	const notGoingCount = occurrence?.rsvps.filter((rsvp) => rsvp.response === 'NOT_GOING').length;
	
	const maybeCount = occurrence?.rsvps.filter((rsvp) => rsvp.response === 'MAYBE').length;
	
	
	// Generate RSVP attendance text
	const rsvpCount = occurrence?.rsvps.length ?? 0;
	const rsvpText =
		rsvpCount === 0
			? 'No RSVPs yet…'
			: `${goingCount ?? 0} going, ${maybeCount ?? 0} maybe, ${notGoingCount ?? 0} not going`;
	
	
	// Initialize
	const eventReachedLimit = Boolean(event?.rsvp?.limit && goingCount && goingCount >= event.rsvp.limit);
	
	
	// Handle close
	const handleClose = useCallback(
		(closeMethod?: 'mouse') => {
			closeMethodRef.current = closeMethod;
			navigate('../..', { relative: 'path', state: { disableScrollToTop: true } });
		},
		[navigate]
	);
	
	
	// Return JSX
	return (
		<Modal identifier='event-details' title={event?.title ?? ''} wide onClose={handleClose}>
			{event?.title && (
				<Helmet>
					<title>{[event.title, pageTitle, communityName].filter(Boolean).join(' | ')}</title>
				</Helmet>
			)}
			
			<Details>
				{occurrence && website && event && (
					<When>
						<Tippy {...tippyProps} content='When'>
							<FontAwesomeIcon icon={faCalendarRange} className='is-gray' />
						</Tippy>
						{formatDateRange({
							start: new Date(occurrence.start),
							end: occurrence.end ? new Date(occurrence.end) : undefined,
							dateFormat: website.formatting.dateFormat,
							timeFormat: website.formatting.timeFormat,
							allDay: event.allDay,
						})}
					</When>
				)}
				
				{event?.location && (
					<Location>
						<Tippy {...tippyProps} content='Location'>
							<FontAwesomeIcon icon={faLocationDot} className='is-gray' />
						</Tippy>
						
						{event.location}
					</Location>
				)}
				
				{event?.rsvp?.allow && (
					<Attendance
						isOpen={showRSVPResponses}
						hasResponses={rsvpCount !== 0}
						onClick={() => setShowRSVPResponses(rsvpCount !== 0 && !showRSVPResponses)}
					>
						<Tippy {...tippyProps} content='RSVPs'>
							<FontAwesomeIcon icon={faUsers} className='is-gray' />
						</Tippy>
						
						{rsvpText}
						
						{rsvpCount !== 0 && <FontAwesomeIcon icon={faChevronRight} className='is-gray' />}
					</Attendance>
				)}
			</Details>
			<Wrapper>
				{event?.rsvp?.allow && (
					<React.Fragment>
						<ResponsesTable
							key={`${event.id}_${occurrence?.id ?? ''}`}
							responses={occurrence?.rsvps ?? []}
							isOpen={showRSVPResponses}
						/>
						<RSVP hasLimit={(event.rsvp.limit ?? 0) > 0}>
							{user.signedIn ? (
								<RSVPButtonGroup>
									<ConditionalTooltip
										content='Max attendees has already been reached'
										show={eventReachedLimit && currentRSVPResponse !== 'GOING'}
									>
										{event.rsvp.prompt && currentRSVPResponse !== 'GOING' ? (
											<Button
												type='button'
												color='plain'
												border
												onClick={() => setShowRSVPPrompt((prevState) => !prevState)}
												smaller
											>
												<FontAwesomeIcon icon={faCheck} size='sm' />
												Going
											</Button>
										) : (
											<APIButton
												options={getRSVPButtonOptions('GOING')}
												onCompletion={() => {
													memoizedLoadOccurrence();
													
													if (rsvpCount <= 1) {
														setShowRSVPResponses(false);
													}
												}}
												{...(currentRSVPResponse === 'GOING' ? {} : { parameters: { response: 'GOING' } })}
												border
												color={currentRSVPResponse === 'GOING' ? 'success' : 'plain'}
												smaller
											>
												<FontAwesomeIcon icon={faCheck} size='sm' />
												Going
											</APIButton>
										)}
									</ConditionalTooltip>
									<APIButton
										options={getRSVPButtonOptions('NOT_GOING')}
										onCompletion={() => {
											memoizedLoadOccurrence();
											setShowRSVPPrompt(false);
											
											if (rsvpCount <= 1) {
												setShowRSVPResponses(false);
											}
										}}
										{...(currentRSVPResponse === 'NOT_GOING' ? {} : { parameters: { response: 'NOT_GOING' } })}
										color={currentRSVPResponse === 'NOT_GOING' ? 'danger' : 'plain'}
										border
										smaller
									>
										<FontAwesomeIcon icon={faX} size='sm' />
										Not going
									</APIButton>
									<APIButton
										options={getRSVPButtonOptions('MAYBE')}
										onCompletion={() => {
											memoizedLoadOccurrence();
											setShowRSVPPrompt(false);
											
											if (rsvpCount <= 1) {
												setShowRSVPResponses(false);
											}
										}}
										{...(currentRSVPResponse === 'MAYBE' ? {} : { parameters: { response: 'MAYBE' } })}
										color={currentRSVPResponse === 'MAYBE' ? 'yellow' : 'plain'}
										border
										smaller
									>
										<FontAwesomeIcon icon={faQuestion} size='sm' />
										Maybe
									</APIButton>
								</RSVPButtonGroup>
							) : (
								<em>
									Please <SignInAnchor>sign in</SignInAnchor> to RSVP
								</em>
							)}
							
							<AnimateHeight height={showRSVPPrompt ? 'auto' : 0} duration={400} animateOpacity>
								<APIForm
									options={formOptions}
									onCancel={() => setShowRSVPPrompt(false)}
									onCompletion={() => {
										memoizedLoadOccurrence();
										setShowRSVPPrompt(false);
									}}
								>
									<Input
										name='answer'
										label={event.rsvp.prompt}
										type='text'
										maxLength={200}
										required={showRSVPPrompt}
										bindSetter={(setter) => (promptAnswerSetter.current = setter)}
									/>
								</APIForm>
							</AnimateHeight>
							
							{event.rsvp.limit && (
								<Limit>
									<em>
										Limited to {event.rsvp.limit} attendee{event.rsvp.limit > 1 ? 's' : ''}
									</em>
								</Limit>
							)}
						</RSVP>
					</React.Fragment>
				)}
				
				{event?.notes || event?.attachments?.length ? (
					<React.Fragment>
						{event?.notes && (
							<React.Fragment>
								<hr />
								<Heading>Notes</Heading>
								
								<Notes dangerouslySetInnerHTML={sanitizedNotes} hasAttachment={(event?.attachments?.length ?? 0) > 0} />
							</React.Fragment>
						)}
						
						{event?.attachments?.length && (
							<React.Fragment>
								<hr />
								<Attachments id={eventID} attachmentURIPrefix='event' attachments={event.attachments} />
							</React.Fragment>
						)}
					</React.Fragment>
				) : null}
			</Wrapper>
		</Modal>
	);
};

export default EventDetailsModal;
